<template>
  <HgModal v-model="internalModel" @close="onClose">
    <template #title>{{ t('agency.userManagement.resetMfa.modal.title') }} </template>
    <template #default>
      <div>
        <p>
          {{ t('agency.userManagement.resetMfa.modal.description') }}
        </p>
      </div>
    </template>
    <template #footer>
      <HgButton
        data-cy="modal-cancel-btn"
        :disabled="isLoading"
        theme="primary-bordered"
        @click="onClose"
      >
        {{ t('deactivation.modal.cancel') }}
      </HgButton>
      <HgButton data-cy="modal-action-btn" :disabled="isLoading" @click="onActionButton">
        {{ t('agency.userManagement.resetMfa.buttonTitle') }}
      </HgButton>
    </template>
  </HgModal>
</template>

<script lang="ts" setup>
import { HgButton, HgModal } from '@smg-real-estate/domus';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { resetMfaEnrollments } from '@/api/user';
import { ModalIds, type Modal } from '@/components/ModalManager/types';
import { ToastTheme } from '@/store/app/AppStore';
import { useStore } from 'vuex';

interface Props {
  modelValue: boolean;
}
const props = defineProps<Props>();

const emit = defineEmits(['update:modelValue', 'close']);

const store = useStore();
const { t } = useI18n();
const isLoading = ref<boolean>(false);

const currentUserId = computed<string>(() => {
  const resetMfaModal = store.getters.getModals.find(
    (modal: Modal) => modal.id === ModalIds.ResetMfaModal,
  );
  return resetMfaModal?.options?.userId as string;
});

const internalModel = computed({
  get() {
    return props.modelValue;
  },
  set(val: boolean) {
    emit('update:modelValue', val);
  },
});

watch(
  () => props.modelValue,
  (val) => {
    if (!val) {
      resetData();
    }
  },
);

const resetData = () => {
  isLoading.value = false;
};

const onClose = () => {
  resetData();
  store.dispatch('closeModal', { id: ModalIds.ResetMfaModal });
};

const showToast = (title: string, message: string, theme: ToastTheme) => {
  store?.dispatch('showToast', {
    title,
    message,
    theme,
  });
};

const onActionButton = async () => {
  if (isLoading.value) return;

  try {
    isLoading.value = true;
    const result = await resetMfaEnrollments(currentUserId.value);

    if (result) {
      showToast(
        t('agency.userManagement.resetMfa.toast.successTitle'),
        t('agency.userManagement.resetMfa.toast.successMessage'),
        ToastTheme.Success,
      );
    } else {
      showToast(
        t('agency.costReport.downloadError.title'),
        t('agency.userManagement.resetMfa.toast.failureMessage'),
        ToastTheme.Danger,
      );
    }
  } catch (error) {
    console.error('Failed to reset MFA:', error);
    showToast(
      t('agency.costReport.downloadError.title'),
      t('agency.userManagement.resetMfa.toast.failureMessage'),
      ToastTheme.Danger,
    );
  } finally {
    onClose();
  }
};
defineExpose({
  isLoading,
  currentUserId,
  internalModel,
});
</script>
